import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { maybeFormatData } from "../../helpers";
import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import DropdownBox from "../DropdownBox";
import ItemActions from "../ItemActions";
import { useToast } from "../../ToastsContext";
import { updateRecord } from "../../helpers";
import { useQueryClient, useMutation } from "@tanstack/react-query";

import { useAuth } from "../../AuthContext";

import "./style.css";

const DataTableRow = ({ item, selectedItems, settings, setSettings, setSelectedItems, data: items, columns, type, functions = {} }) => {
	const editable = ["ticket_number", "invoice_number", "transport_invoice_number", "transport"];

	const { authToken } = useAuth();
	const queryClient = useQueryClient();
	const { addToast, clearToasts } = useToast();

	const maybeUpdateRecord = async (data) => {
		return await updateRecord({ recordId: data.id, data, authToken });
	};

	const mutation = useMutation({
		mutationFn: maybeUpdateRecord,
		onSuccess: (data) => {
			// Update the cache with the new user data
			queryClient.setQueryData(["record", data.id, authToken], data);
		},
	});

	const maybeSaveContentEdit = async (e, column, original, item) => {
		const value = e.target.innerText;

		if (value === "") {
			e.target.classList.add("no-data");
		} else {
			e.target.classList.remove("no-data");
		}

		if (value !== original) {
			const data = item;
			data[column.value] = value;
			clearToasts();
			e.target.classList.remove("is-success");
			e.target.classList.remove("is-error");
			e.target.classList.add("is-loading");

			try {
				await mutation.mutateAsync(data);
				await queryClient.invalidateQueries({ queryKey: [`records`, item.id], refetchType: "active" });
				addToast({ title: `Record Updated`, message: "The record has been updated", type: "success" });
				e.target.classList.add("is-success");
			} catch (error) {
				console.log(error);
				addToast({ title: error?.code ?? "Unable to update the record", message: error.message, type: "error" });
				e.target.classList.add("is-error");
			}

			e.target.classList.remove("is-loading");
		}
	};

	return (
		<tr>
			<td className={"table-row-actions"}>
				<DropdownBox forward icon={faEllipsisVertical} options={{ align: "start", side: "right", triggerClassName: "table-single-actions-trigger", contentClassName: "table-single-actions" }}>
					<ItemActions {...{ item, settings, setSettings, type, functions, items, buttons: { showView: true } }} />
				</DropdownBox>

				<input
					type="checkbox"
					value={item.id}
					checked={selectedItems.includes(item.id)}
					onChange={() => {
						if (selectedItems.includes(item.id)) {
							setSelectedItems(selectedItems.filter((id) => id !== item.id));
						} else {
							setSelectedItems([...selectedItems, item.id]);
						}
					}}
				/>
			</td>
			{columns.map((column, i) => {
				const formatted = maybeFormatData(item, column.value, items, type);
				return (
					<td key={i}>
						{formatted && column?.icon && <FontAwesomeIcon icon={column.icon} />}
						{formatted ? (
							<span
								onBlur={(e) => {
									maybeSaveContentEdit(e, column, formatted, item);
								}}
								contentEditable={editable.includes(column.value) && "plaintext-only"}
								suppressContentEditableWarning={true}
							>
								{formatted}
							</span>
						) : (
							<span
								onBlur={(e) => {
									maybeSaveContentEdit(e, column, formatted, item);
								}}
								contentEditable={editable.includes(column.value) && "plaintext-only"}
								suppressContentEditableWarning={true}
								className="no-data"
							></span>
						)}
					</td>
				);
			})}
		</tr>
	);
};

const DataTable = ({ data, type, preferences, selectedItems = [], settings, setSettings, setSelectedItems, functions, defaultColumns }) => {
	const columns = preferences?.columns?.length ? preferences.columns : defaultColumns;

	return (
		<div className="data-table">
			<table>
				<thead>
					<tr>
						<th>
							<input
								type="checkbox"
								value="true"
								checked={selectedItems.length === data.length}
								onChange={() => {
									const ids = data.map((item) => item.id);
									if (selectedItems.length === data.length) {
										setSelectedItems([]);
									} else {
										setSelectedItems(ids);
									}
								}}
							/>
						</th>
						{columns.map((column, i) => {
							return <th key={i}>{column.label}</th>;
						})}
					</tr>
				</thead>
				<tbody>
					{data.map((item, key) => {
						return <DataTableRow {...{ columns, item, settings, setSettings, functions, selectedItems, setSelectedItems, preferences, data, type }} key={key} />;
					})}
				</tbody>
			</table>
		</div>
	);
};

export default DataTable;
